import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, BookOpen, Clock, Download, Printer } from "lucide-react";
import { PosterScanLine, PosterNetworkInfo, PosterDidYouKnow, PosterHeadline } from "../components/extract/PosterInfo";
import { fetchExtract, fetchPlacement, extractPublicUrl } from "../lib/api";

const Rule = () => <div className="h-px bg-[#D1CFC7] my-2" />;

export default function ExtractPoster() {
  const { slug } = useParams();
  const [extract, setExtract] = useState(null);
  const [error, setError] = useState(false);
  const [placement, setPlacement] = useState(null);
  const lieu = useSearchParams()[0].get("lieu");
  const url = extractPublicUrl(slug, lieu);

  useEffect(() => { fetchExtract(slug, false).then(setExtract).catch(() => setError(true)); }, [slug]);
  useEffect(() => { if (lieu) fetchPlacement(slug, lieu).then(setPlacement).catch(() => setError(true)); }, [slug, lieu]);

  const downloadQR = () => {
    const a = document.createElement("a");
    a.href = document.getElementById("qr-download-canvas").toDataURL("image/png");
    a.download = `qr-${slug}${lieu ? `-${lieu}` : ""}.png`;
    a.click();
  };

  if (error) return <p data-testid="poster-error" className="p-12 font-body text-[#9E4735]">Extrait introuvable ou non publié.</p>;
  if (!extract || (lieu && !placement)) return <p className="p-12 font-body text-[#757575]">Chargement…</p>;

  return (
    <div data-testid="page-extract-poster" className="min-h-screen bg-[#E8E4D9] py-10 px-4 print:p-0 print:bg-white">
      <div className="no-print max-w-[210mm] mx-auto mb-6 flex flex-wrap items-center gap-3">
        <Link to="/admin" data-testid="poster-back-link" className="flex items-center gap-2 text-sm font-body text-[#1A1A1A] mr-auto"><ArrowLeft size={14} /> Tableau de bord</Link>
        <button onClick={downloadQR} data-testid="poster-download-qr" className="flex items-center gap-2 border border-[#1A1A1A] px-4 py-2 text-sm font-body hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors"><Download size={14} /> QR code (PNG)</button>
        <button onClick={() => window.print()} data-testid="poster-print-button" className="flex items-center gap-2 bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body hover:bg-[#1F2D21] transition-colors"><Printer size={14} /> Imprimer l'affiche</button>
        <QRCodeCanvas id="qr-download-canvas" value={url} size={1024} marginSize={4} className="hidden" />
      </div>
      {placement && <p data-testid="poster-lieu-label" className="no-print max-w-[210mm] mx-auto mb-3 text-xs font-body text-[#4A4A4A]">Affiche du lieu : <strong>{placement.name}</strong>{placement.city ? ` — ${placement.city}` : ""}</p>}
      <PosterSheet extract={extract} sponsor={placement || { name: extract.partner_name, logo_url: extract.partner_logo_url }} url={url} />
    </div>
  );
}

const PosterSheet = ({ extract, sponsor, url }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  return (
  <article data-testid="poster-sheet" className="poster-sheet mx-auto bg-[#FAF9F6] w-full max-w-[210mm] aspect-[210/297] shadow-xl print:shadow-none p-[5%] flex flex-col text-[#1A1A1A]">
    <div className="border border-[#2C402E]/40 flex-1 flex flex-col justify-evenly px-[8%] py-[4%]">
      <div className="flex items-center justify-between gap-4 h-[64px] sm:h-[120px]">
        <img src="/cled-logo-cropped.png" alt="Challenges Littéraires Éditions" className="h-[70%] w-auto object-contain mix-blend-multiply" />
        {sponsor.logo_url && !logoFailed ? (
          <img src={sponsor.logo_url} alt={sponsor.name || "Partenaire"} onError={() => setLogoFailed(true)} data-testid="poster-partner-logo" className="h-full w-auto max-w-[40%] object-contain" />
        ) : sponsor.name ? (
          <p data-testid="poster-partner-name" className="font-serif italic text-xl sm:text-3xl leading-tight text-[#2C402E] text-right max-w-[50%]">{sponsor.name}</p>
        ) : null}
      </div>
      <Rule />
      <div className="text-center">
        <PosterHeadline />
        <PosterDidYouKnow />
      </div>
      <Rule />
      <div className="flex flex-col items-center text-center">
        <div className="relative p-4 sm:p-5 bg-white border border-[#D1CFC7]">
          <QRCodeSVG value={url} size={512} marginSize={0} fgColor="#1A1A1A" className="w-[38vw] max-w-[230px] sm:w-[230px] h-auto print:w-[52mm]" data-testid="poster-qr-code" />
        </div>
        <p className="font-serif italic text-base sm:text-xl text-[#2C402E] mt-[3%]">« {extract.title} » — {extract.author}</p>
        <PosterScanLine />
      </div>
      <Rule />
      <div className="text-center font-body">
        <p className="flex items-center justify-center gap-2 text-xs sm:text-sm text-[#1A1A1A] mb-[3%]"><Clock size={14} className="text-[#9E4735]" /> Temps de lecture : {extract.reading_time}</p>
        <PosterNetworkInfo />
        <p className="text-[9px] sm:text-[10px] text-[#757575] mt-[2%] break-all" data-testid="poster-url">{url.replace(/^https?:\/\//, "")}</p>
      </div>
    </div>
  </article>
);
};
